"use client";

import { useLazyQuery, useQuery, useSubscription } from "@apollo/client";
import { WIDTH_EXPLORE, WIDTH_MD } from "@constants/global";
import FilterButtons from "components/Explore/FilterButtons";
import ScrollToTop from "components/Explore/filterButtons/ui/v2/scrollToTop";
import LazyMessageRenderer from "components/Explore/timeLine/LazyMessageRenderer";
import { EventCardSlider } from "components/Explore/timeLine/EventCardSlider";
import NewMessage from "components/_Pages/Explore/NewMessage";
import { ExploreStoriesInline } from "components/_Pages/Explore/NewMessage/ExploreStoriesInline";
import SvgWithOutData from "components/core/SvgWithOutData/SvgWithOutData";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import { gqlMessage, gqlUser } from "gql";
import { IGetMessagesInput, IMessagesPaginated } from "gql/Message/queries";
import useTranslation from "next-translate/useTranslation";
import { useCallback, useEffect, useRef, useState } from "react";
import { forceRefetchQueries } from "settings/apollo";
import { ExploreEmptyStateV2 } from "components/ui/v2";
import useCapsuleTagsStore from "stores/capsuleTags/useCapsuleTagsStore";
import { EMessageType, IMessage } from "types/Message.type";
import { IUser } from "types/User.types";
import useWindowSize from "utils/hooks/useWindowSize";

const STACKABLE_TYPES = new Set([
  EMessageType.CORPORATE_VALUE,
  EMessageType.ENDORSEMENT,
  EMessageType.CERTIFICATE,
  EMessageType.BIRTHDAY,
  EMessageType.ANNIVERSARY,
  EMessageType.NEW_USERS
]);

const sameDay = (
  a: Date | string | undefined,
  b: Date | string | undefined
): boolean => {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

const groupMessagesByType = (
  messages: IMessage[]
): Array<IMessage | IMessage[]> => {
  if (messages.length === 0) return [];

  const grouped: Array<IMessage | IMessage[]> = [];
  let currentGroup: IMessage[] = [];
  let currentType: EMessageType | null = null;

  for (const message of messages) {
    const lastInGroup = currentGroup[currentGroup.length - 1];
    const shouldStack =
      currentGroup.length > 0 &&
      currentType === message.type &&
      STACKABLE_TYPES.has(message.type) &&
      sameDay(lastInGroup?.createdAt, message.createdAt);

    if (shouldStack) {
      currentGroup.push(message);
    } else {
      if (currentGroup.length > 0) {
        grouped.push(
          currentGroup.length === 1 ? currentGroup[0] : [...currentGroup]
        );
      }
      currentGroup = [message];
      currentType = message.type;
    }
  }

  if (currentGroup.length > 0) {
    grouped.push(
      currentGroup.length === 1 ? currentGroup[0] : [...currentGroup]
    );
  }

  return grouped;
};

export default function TimeLinePanel() {
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [totalMessages, setTotalMessages] = useState<number>(0);
  const [mentionUsers, setMentionUsers] = useState<IUser[]>([]);
  const [filterType, setFilterType] = useState("all");
  const { user } = useUserStoreContext((store) => ({
    user: store.user
  }));
  const { t } = useTranslation("explore");
  const { vendor } = useVendor();
  const wWidth = useWindowSize()?.width;
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [preloadingInitialMessages] = useState(false);
  const isMd = wWidth >= WIDTH_EXPLORE;
  const isSm = wWidth < WIDTH_MD;
  const targetRefTop = useRef<HTMLDivElement>(null);
  const [selectedFilterTagValues, setSelectedFilterTagValues] = useState<
    string[]
  >([]);
  const didRunCapsuleFilterEffect = useRef(false);

  const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Load user mentions immediately when component mounts since they're needed for message rendering
  const { loading: usersLoading } = useQuery<{
    vendorUserList: IUser[];
  }>(gqlUser.queries.USER_TO_MENTION, {
    variables: {
      vendorUserListInput: {}
    },
    skip: !user, // Only skip when user is not loaded
    fetchPolicy: "cache-and-network", // Return cache immediately but also fetch from network
    onCompleted: ({ vendorUserList }) => {
      setMentionUsers(vendorUserList);
    }
  });

  useSubscription(gqlMessage.subscriptions.MESSAGE_CREATED, {
    skip: !user,
    variables: {
      vendor: user?.selectedVendorId
    },
    onData: async ({ data, client }) => {
      const { cache: _cache } = client;
      const { messageCreated } = data?.data;

      const newMessage = {
        ...messageCreated,
        createdAt: new Date()
      };

      // Only add to local state, don't update cache to avoid refetching
      setMessages((prevMessages) => [newMessage, ...prevMessages]);
    }
  });

  const [getMessages, { loading: loadingMessages, data: firstLoadingMessges }] =
    useLazyQuery<
      {
        messagesPaginated: IMessagesPaginated;
      },
      {
        getMessagesInput: IGetMessagesInput;
      }
    >(gqlMessage.queries.GET_MESSAGES, {
      fetchPolicy: "cache-and-network", // Return cache immediately but also fetch from network
      nextFetchPolicy: "cache-and-network", // Also fetch from network for pagination to ensure fresh data
      onCompleted: async ({ messagesPaginated }) => {
        setTotalMessages(messagesPaginated?.total || 0);

        if (messagesPaginated?.messages.length > 0) {
          const auxMessages = [...messages, ...messagesPaginated.messages];
          // * remove duplicated from auxMessages
          const uniqueIds = new Set();
          const uniqueMessages: IMessage[] = [];

          for (const obj of auxMessages) {
            if (!uniqueIds.has(obj.id)) {
              uniqueMessages.push(obj);
              uniqueIds.add(obj.id);
            }
          }
          // * check if filterType is Capsules then show filtered messages and return
          if (filterType === EMessageType.CAPSULES) {
            setMessages(messagesPaginated?.messages);
            // Check if we've reached the end after setting messages
            if (
              messagesPaginated.messages.length === messagesPaginated?.total
            ) {
              setIsFinished(true);
            }
            return;
          }

          // * check if filterType is selected
          if (filterType !== "all" && filterType !== EMessageType.CAPSULES) {
            const filteredMessages = uniqueMessages.filter(
              (message) => message.type === filterType
            );
            setMessages(filteredMessages);
            // Check if we've reached the end for filtered messages
            if (filteredMessages.length >= messagesPaginated?.total) {
              setIsFinished(true);
            }
          } else {
            setMessages([...uniqueMessages]);
            // Check if we've reached the end after setting all messages
            if (uniqueMessages.length >= messagesPaginated?.total) {
              setIsFinished(true);
            }
          }
        } else {
          const auxMessages = [...messages, ...messagesPaginated?.messages];
          // * remove duplicated from auxMessages by id
          const uniqueIds = new Set();
          const uniqueMessages: IMessage[] = [];

          for (const obj of auxMessages) {
            if (!uniqueIds.has(obj.id)) {
              uniqueMessages.push(obj);
              uniqueIds.add(obj.id);
            }
          }

          // * check if filterType is selected
          if (filterType !== "all") {
            const filteredMessages = uniqueMessages.filter(
              (message) => message.type === filterType
            );
            setMessages(filteredMessages);
            // Check if we've reached the end for filtered messages
            if (filteredMessages.length >= messagesPaginated?.total) {
              setIsFinished(true);
            }
          } else {
            setMessages([...uniqueMessages]);
            // Check if we've reached the end after setting all messages
            if (uniqueMessages.length >= messagesPaginated?.total) {
              setIsFinished(true);
            }
          }
        }

        // If no new messages were returned, we've reached the end
        if (messagesPaginated?.messages.length === 0) {
          setIsFinished(true);
        }
      },
      onError: (error) => {
        console.error("🚀 ~ error", error);
      }
    });

  const handleGetMessages = useCallback(() => {
    if (loadingMessages || isFinished) {
      return;
    }

    getMessages({
      variables: {
        getMessagesInput: {
          skip: messages.length,
          take: 5,
          type: filterType
        }
      }
    });
  }, [
    loadingMessages,
    isFinished,
    messages.length,
    totalMessages,
    filterType,
    getMessages
  ]);

  // * handle filter messages by capsules
  const handleFilterMessagesCapsules = (filters: string[]) => {
    if (loadingMessages) return;
    setIsFinished(false); // Reset finished state when changing capsule filters
    getMessages({
      variables: {
        getMessagesInput: {
          skip: 0,
          take: 5,
          type: filterType,
          tags: filters // * only get messages with tags node
        }
      }
    });
  };

  // * handle filter messages by capsules when selectedFilterTagValues changes
  useEffect(() => {
    if (filterType !== EMessageType.CAPSULES) return;

    if (!didRunCapsuleFilterEffect.current) {
      didRunCapsuleFilterEffect.current = true;
      return;
    }

    handleFilterMessagesCapsules(selectedFilterTagValues);
  }, [selectedFilterTagValues]);

  const filteredMessages = async (filter: string) => {
    setFilterType(filter);
    setMessages([]);
    setIsFinished(false); // Reset finished state when changing filters
    didRunCapsuleFilterEffect.current = filter === EMessageType.CAPSULES;

    // * force to get messages
    forceRefetchQueries([gqlMessage.queries.GET_MESSAGES]);

    await getMessages({
      variables: {
        getMessagesInput: {
          skip: 0,
          take: 5,
          type: filter
        }
      }
    });
  };

  // * get all tags for capsules...
  const { dataAllCapsuleTags } = useCapsuleTagsStore();

  // Load tags only when capsules filter is active or when filter changes to capsules
  useEffect(() => {
    if (
      filterType === EMessageType.CAPSULES &&
      (!dataAllCapsuleTags || dataAllCapsuleTags.length === 0)
    ) {
      // Load tags only when needed
      // This will be handled by a separate lazy query when filter is changed to CAPSULES
    }
  }, [filterType, dataAllCapsuleTags?.length]);

  // Remove the immediate tags query to reduce initial load

  useEffect(() => {
    setIsFinished(false); // Reset finished state on component mount
    handleGetMessages(); // Initial load
  }, []);

  const observer = useRef<IntersectionObserver | null>(null);
  const lastMessageElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loadingMessages) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            !isFinished &&
            messages.length < totalMessages
          ) {
            handleGetMessages();
          }
        },
        {
          rootMargin: "50px" // Trigger slightly before reaching the bottom
        }
      );
      if (node) observer.current.observe(node);
    },
    [
      loadingMessages,
      isFinished,
      messages.length,
      totalMessages,
      handleGetMessages
    ]
  );

  const teamAsUsersToMention =
    user?.teams?.map((team) => ({
      id: team.id,
      name: team.name,
      lastname: "[TEAM]"
    })) ?? [];

  // Group messages for stacking
  const groupedMessages = groupMessagesByType(messages);

  // Helper function to render a single message or stacked messages
  const renderMessage = (
    messageOrGroup: IMessage | IMessage[],
    index: number
  ) => {
    const isLastMessage = index === groupedMessages.length - 1;

    if (Array.isArray(messageOrGroup)) {
      const stackId = `stacked-${messageOrGroup[0].id}`;
      return (
        <div key={stackId} ref={isLastMessage ? lastMessageElementRef : null}>
          <EventCardSlider
            messages={messageOrGroup}
            mentionUsers={mentionUsers}
            allMessages={messages}
            setMessages={setMessages}
            vendor={vendor}
          />
        </div>
      );
    }

    // Single message
    return (
      <div
        id={`message-${index + 1}`}
        key={messageOrGroup.id}
        ref={isLastMessage ? lastMessageElementRef : null}
        className="message"
      >
        {renderSingleMessage(messageOrGroup)}
      </div>
    );
  };

  // Helper function to render a single message
  const renderSingleMessage = (message: IMessage) => {
    return (
      <LazyMessageRenderer
        message={message}
        mentionUsers={mentionUsers}
        messages={messages}
        setMessages={setMessages}
        vendor={vendor}
      />
    );
  };

  return (
    <div
      className="relative mt-2 grid min-w-0 max-w-full grid-cols-[minmax(0,1fr)] content-start gap-3 overflow-x-clip px-2 pb-8 sm:mt-3 sm:gap-4 sm:px-3 md:px-4 lg:px-4"
      id="explore-timeline"
      ref={targetRefTop}
    >
      <div className="mb-0">
        <ExploreStoriesInline />
      </div>

      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <NewMessage
            usersToMention={[
              ...mentionUsers,
              ...(teamAsUsersToMention as IUser[])
            ]}
            usersLoading={usersLoading}
          />
        </div>
        <FilterButtons
          filterType={filterType}
          isMd={isMd}
          isSm={isSm}
          loadingMessages={loadingMessages}
          vendor={vendor || undefined}
          filteredMessages={filteredMessages}
          t={t}
          selectedFilterTagValues={selectedFilterTagValues}
          setSelectedFilterTagValues={setSelectedFilterTagValues}
          tags={dataAllCapsuleTags}
          handleScrollToTop={() => scrollToRef(targetRefTop)}
        />
      </div>

      {(loadingMessages || preloadingInitialMessages) &&
        messages.length === 0 &&
        [0, 1, 2]?.map((item) => (
          <div
            key={item}
            role="status"
            aria-label={t("Loading message")}
            className="w-full rounded-2xl border border-slate-200/60 p-4 shadow-[0_1px_3px_rgba(0,0,0,0.045),0_6px_18px_-8px_rgba(0,0,0,0.09)] dark:border-zinc-800/80 dark:bg-zinc-900 sm:p-5"
          >
            <div className="animate-pulse space-y-4">
              {/* Header skeleton */}
              <div className="flex items-center space-x-4">
                <div className="relative h-12 w-12 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                </div>
                <div className="flex-1 space-y-2">
                  <div className="relative h-3 w-32 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                  </div>
                  <div className="relative h-2 w-20 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                  </div>
                </div>
              </div>
              {/* Content skeleton */}
              <div className="space-y-3">
                <div className="relative h-4 w-full overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                </div>
                <div className="relative h-4 w-5/6 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                </div>
                <div className="relative h-4 w-3/4 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                </div>
              </div>
              {/* Footer skeleton */}
              <div className="flex items-center justify-between pt-4">
                <div className="flex space-x-4">
                  <div className="relative h-8 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                  </div>
                  <div className="relative h-8 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                  </div>
                </div>
                <div className="relative h-6 w-12 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                </div>
              </div>
            </div>
            <span className="sr-only">{t("Loading content...")}</span>
          </div>
        ))}
      {messages.length === 0 &&
        !loadingMessages &&
        !preloadingInitialMessages &&
        firstLoadingMessges?.messagesPaginated.messages.length === 0 && (
          <div className="mt-4 grid gap-4">
            <div className="mb-1">
              <ExploreEmptyStateV2>
                <SvgWithOutData
                  variant="explore-withOut-content"
                  title={`${t("¡Te damos la bienvenida a Groowcity!")}`}
                  description={`${t("Rompamos el hielo, comparte un mensaje y se parte de la comunidad")}`}
                />
              </ExploreEmptyStateV2>
            </div>
          </div>
        )}
      <div className="flex flex-col gap-5 sm:gap-6">
        {groupedMessages.map((messageOrGroup, index) =>
          renderMessage(messageOrGroup, index)
        )}
      </div>

      {messages.length > 0 &&
        messages.length < totalMessages &&
        !isFinished && (
          <div
            className="flex justify-center py-6 md:py-8"
            id="load-more-button"
            role="status"
            aria-label={t("Loading more messages")}
          >
            <div className="flex flex-col items-center space-y-3">
              {loadingMessages ? (
                <div className="relative">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600 dark:border-blue-800 dark:border-t-blue-400"></div>
                  <div className="absolute inset-0 h-8 w-8 animate-pulse rounded-full bg-blue-100 opacity-20 dark:bg-blue-900"></div>
                </div>
              ) : (
                <div className="relative h-2 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10"></div>
                </div>
              )}
              {loadingMessages && (
                <p className="animate-pulse text-xs text-slate-500 dark:text-zinc-400 sm:text-sm">
                  {t("Cargando más mensajes")}
                </p>
              )}
            </div>
          </div>
        )}

      {/* put a finished message when is isFinished */}
      {isFinished && messages?.length > 0 && (
        <div className="mb-4">
          <ExploreEmptyStateV2>
            <SvgWithOutData
              variant="explore-withOut-content"
              title={`${t("¡Te damos la bienvenida a Groowcity!")}`}
              description={`${t("Rompamos el hielo, comparte un mensaje y se parte de la comunidad")}`}
            />
          </ExploreEmptyStateV2>
        </div>
      )}

      <div className="sticky bottom-6 flex justify-end">
        <ScrollToTop handleScrollToTop={() => scrollToRef(targetRefTop)} />
      </div>
    </div>
  );
}
